<template>
  <div class="loading-page">
    <nice-icon class="loading-page-logo" icon="logo"></nice-icon>
    <div class="loading-page-message" v-translate>Loading...</div>
  </div>
</template>

<script setup>
import { onMounted } from "vue";
import { useGeneralStore } from "@/store/general.store";
import { useAuthStore } from "@/store/auth.store";
import { useRoute, useRouter } from "vue-router";
import StorageService from "@/services/storage.service";
import ApiService from "@/services/api/api.service";
const generalStore = useGeneralStore();
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();

onMounted(async () => {
  if (!authStore.loggedIn) {
    return (window.location.href = "/");
  }

  // Fetch user profile
  await authStore.getUserProfile();

  // Fetch organizations
  const organizations = await generalStore.getOrganizations();

  async function handleOrganizations() {
    if (route.params.organizationId === undefined) {
      const savedId = StorageService.getOrganization();
      if (savedId != null) {
        // Read from cookie
        try {
          const organization = await generalStore.getOrganization(savedId);
          route.params.organizationId = organization.value.id;

          // Fetch event
          await handleEvents();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
          StorageService.removeOrganization();
          return handleOrganizations();
        }
      } else {
        if (organizations.value.length) {
          // Select first organization
          await generalStore.setOrganization(organizations.value[0]);
          route.params.organizationId = organizations.value[0].id;

          // Fetch event
          await handleEvents();
        } else {
          // No organizations - go to no organizations view
          return router.push({ name: "no-organizations" });
        }
      }
    } else {
      // Fetch organization
      await generalStore.getOrganization(route.params.organizationId);

      // Fetch data
      await handleEvents();
    }
  }

  async function handleEvents() {
    if (route.params.eventId === undefined) {
      const savedId = StorageService.getEvent();
      if (savedId != null) {
        // Read from cookie
        try {
          const event = await generalStore.getEvent(route.params.organizationId, savedId);
          route.params.eventId = event.value.id;

          // Fetch data
          await generalStore.getData();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
          StorageService.removeEvent();
          return handleOrganizations();
        }
      } else {
        const events = await ApiService.events.getAll(route.params.organizationId);
        if (events.length > 0) {
          // Select first event
          await generalStore.setEvent(events[0]);
          route.params.eventId = events[0].id;

          // Fetch data
          await generalStore.getData();
        } else {
          // No events - go to events list
          return router.push({
            name: "app.organizations.events.all",
            params: { organizationId: route.params.organizationId },
          });
        }
      }
    } else {
      // Fetch event
      await generalStore.getEvent(route.params.organizationId, route.params.eventId);

      // Fetch data
      await generalStore.getData();
    }

    return router.push({
      name: "app.organizations.events.single",
      params: { organizationId: route.params.organizationId, eventId: route.params.eventId },
    });
  }

  return handleOrganizations();
});
</script>

<style lang="scss">
.loading-page {
  text-align: center;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.loading-page .loading-page-message {
  animation: opacity-change 2s infinite ease-in-out;
}

.loading-page .loading-page-logo {
  fill: currentColor;
  width: 115px;
  height: 78px;
  color: var(--primary-color);
  animation: color-change 2s infinite ease-in-out;
}

@keyframes color-change {
  0% {
    color: var(--primary-color);
  }

  50% {
    color: var(--border-color);
  }

  100% {
    color: var(--primary-color);
  }
}

@keyframes opacity-change {
  0% {
    color: var(--font-color);
  }

  50% {
    color: var(--border-color);
  }

  100% {
    color: var(--font-color);
  }
}
</style>
